import _ from 'lodash';

const API_URL = window.__RUNTIME_CONFIG__?.API_HOST ? 'https://' + window.__RUNTIME_CONFIG__.API_HOST : '';

/**
 * @module api/OAuth
 *
 * This module performs all OAuth requests to the Atomic OAuth endpoint.
 */
const OAuth = (function () {
  let audience;
  let clientId;
  const apiPrefix = API_URL + '/oauth';

  const initialize = (_clientId, _audience) => {
    clientId = _clientId;
    audience = _audience;
  };

  const checkResponse = (resp) => {
    if (resp.status >= 200 && resp.status < 300) {
      return resp;
    }
    throw resp;
  };

  const buildFormData = (params, encode = false) => {
    let formData = encode ? '' : `client_id=${clientId}`;
    _.forEach(params, (value, key) => {
      if (encode) {
        formData += `${encodeURIComponent(key)}=${encodeURIComponent(value)}&`;
      } else {
        formData += `&${key}=${value}`;
      }
    });
    return formData;
  };

  const oauthAuthorize = async (params) => {
    const p = { ...params, audience, response_type: 'code', code_challenge_method: 'S256' };
    const url = `${apiPrefix}/authorize?${buildFormData(p)}`;
    try {
      const resp = await fetch(url);
      checkResponse(resp);
      const token = new URL(resp.url).searchParams.get('request_token');
      const errDescription = new URL(resp.url).searchParams.get('error_description');
      const code = new URL(resp.url).searchParams.get('code');
      return { token, error: errDescription, authCode: code };
    } catch (error) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Authroize Failed', error);
      }
      throw error;
    }
  };

  const login = async (params) => {
    const url = `${apiPrefix}/login?client_id=${clientId}`;
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: buildFormData(params, true),
      });
      checkResponse(resp);
      const authCode = new URL(resp.url).searchParams.get('code');
      const errDescription = new URL(resp.url).searchParams.get('error_description');
      return { authCode, error: errDescription };
    } catch (error) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error Logging In', error);
      }
      throw error;
    }
  };

  const logout = async (redirectURI) => {
    const p = { redirect_uri: redirectURI };
    const url = `${apiPrefix}/logout?${buildFormData(p)}`;
    await fetch(url);
  };

  const oauthTokenGet = async (params) => {
    const p = { ...params, audience, client_id: clientId, grant_type: 'authorization_code' };
    const url = `${apiPrefix}/token`;
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: buildFormData(p, true),
      });
      checkResponse(resp);
      const token = await resp.json();
      return { token };
    } catch (error) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error getting token', error);
      }
      throw error;
    }
  };

  const oauthTokenRefresh = async (params) => {
    const p = { ...params, audience, client_id: clientId, grant_type: 'refresh_token' };
    try {
      const url = `${apiPrefix}/token`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: buildFormData(p, true),
      });
      checkResponse(resp);
      const token = await resp.json();
      return { token };
    } catch (error) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('Error Refreshing Token ', error);
      }
      throw error;
    }
  };

  const userInfo = async (token) => {
    const url = `${apiPrefix}/userInfo`;
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      checkResponse(resp);
      return await resp.json();
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('UserInfo Failed', err);
      }
      throw err;
    }
  };

  const userPrincipal = async (token) => {
    const url = `${apiPrefix}/userPrincipal`;
    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      checkResponse(resp);
      return await resp.json();
    } catch (err) {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('UserPrincipal Failed', err);
      }
      throw err;
    }
  };

  return {
    oauthAuthorize,
    userInfo,
    userPrincipal,
    oauthTokenGet,
    oauthTokenRefresh,
    login,
    logout,
    initialize,
  };
})();

export default OAuth;
